import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { twMerge } from 'tailwind-merge';
import { applyTypography, useIsMobile, useT } from '@repo/common';
import { BaseButton } from '@repo/common';
import PathfinderQuestionHeader from '../layout/PathfinderQuestionHeader';

import { ReactComponent as SearchIcon } from '@repo/common/assets/icons/20px/icon_20px_search.svg';
import { ReactComponent as CancelIcon } from '@repo/common/assets/icons/16px/icon_16px_cancel.svg';
import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-top.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/16px/icon_16px_plus_outline.svg';
import { ReactComponent as LocationIcon20 } from '@repo/common/assets/icons/20px/icon_20px_location-pin_outline.svg';
import { ReactComponent as LocationIcon16 } from '@repo/common/assets/icons/16px/icon_16px_location-pin_outline.svg';

const DEFAULT_SEARCH_PLACEHOLDER = 'Search school';

const normalizeChoice = (option, index) => {
  if (typeof option === 'string') {
    return {
      value: `choice-${index + 1}`,
      label: option,
    };
  }

  return {
    value: option?.value ?? `choice-${index + 1}`,
    label: option?.label ?? '',
  };
};

const normalizeResult = (result, index) => ({
  value: result?.value ?? `result-${index + 1}`,
  title: result?.title ?? result?.label ?? '',
  subtitle: result?.subtitle ?? '',
});

const normalizeLabel = (value = '') => value.trim().toLowerCase();

function PathfinderDetailSearchFilterTrigger({
  label,
  placeholder,
  active = false,
  onClick,
  className = '',
}) {
  const ArrowIcon = active ? ArrowTopIcon : ArrowBottomIcon;

  return (
    <BaseButton
      type='text'
      size='m'
      htmlType='button'
      typo={false}
      isStatic
      gap='gap-2'
      iconPosition='after'
      icon={
        <ArrowIcon
          className='size-4 shrink-0 text-text-placeholder'
          aria-hidden
        />
      }
      onClick={onClick}
      textWrapperClass={twMerge(
        'h-auto flex-none',
        applyTypography('b14', label || placeholder),
        'whitespace-nowrap',
        label ? 'text-text-primary' : 'text-text-placeholder',
      )}
      className={twMerge(
        'h-12 min-w-0 cursor-pointer items-center gap-2 rounded-[15px] border-2 border-transparent bg-surface-lightgray60 px-4 py-3 transition-colors duration-200 ease-out hover:bg-grayscale-gray01 active:bg-grayscale-gray01',
        active ? 'border-surface-green40' : '',
        className,
      )}
    >
      {label || placeholder}
    </BaseButton>
  );
}

function PathfinderDetailSearchFilterPopover({
  type,
  searchPlaceholder,
  query,
  onQueryChange,
  options,
  selectedValue,
  onSelect,
  onUseQuery,
  formatQueryLabel = (nextQuery) => `Use "${nextQuery}" as your ${type}`,
  loading = false,
  inputRef,
  className = '',
}) {
  const t = useT();
  const typeLabelKo = type === 'city' ? '도시' : type === 'country' ? '국가' : type;
  const startTypingText = t(
    `Start typing to search ${type}s`,
    `검색어를 입력해 ${typeLabelKo}를 찾아보세요`,
  );
  const noResultsText = t('No results found', '검색 결과가 없어요');
  const trimmedQuery = query.trim();
  const filteredOptions = useMemo(() => {
    const normalizedQuery = normalizeLabel(query);
    if (!normalizedQuery) return options;
    return options.filter((option) =>
      normalizeLabel(option.label).includes(normalizedQuery),
    );
  }, [options, query]);

  return (
    <div
      className={twMerge(
        'absolute left-0 top-[calc(100%+8px)] z-20 flex h-[254px] w-[300px] flex-col overflow-hidden rounded-[8px] border border-grayscale-gray01 bg-grayscale-gray00 shadow-popup-02',
        className,
      )}
    >
      <div className='flex items-center w-full h-12 gap-2 pl-4 pr-3 border-b border-grayscale-gray01 bg-surface-lightgray40'>
        <LocationIcon20
          className='size-5 shrink-0 text-text-placeholder'
          aria-hidden
        />
        <input
          ref={inputRef}
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && trimmedQuery && onUseQuery) {
              event.preventDefault();
              onUseQuery(trimmedQuery);
            }
          }}
          placeholder={searchPlaceholder}
          className={twMerge(
            applyTypography('b16', query || searchPlaceholder),
            'w-full border-none bg-transparent p-0 text-text-primary outline-none placeholder:text-text-placeholder',
          )}
        />
        {query ? (
          <BaseButton
            type='text'
            size='s'
            htmlType='button'
            onClick={() => onQueryChange('')}
            className='size-5 min-w-0 shrink-0 rounded-[30px] border-0 p-0 bg-grayscale-gray01 text-text-placeholder transition-colors duration-200 ease-out hover:bg-grayscale-gray02 hover:text-text-placeholder'
            aria-label={`Clear ${type} search`}
            icon={<CancelIcon className='size-4' aria-hidden />}
          />
        ) : null}
      </div>

      <div className='flex flex-col flex-1 min-h-0 p-2'>
        {loading ? (
          <div className='flex flex-1 items-center justify-center pb-[30px]'>
            <div className='size-10 rounded-full border-[3px] border-grayscale-gray02 border-t-grayscale-gray04 animate-spin' />
          </div>
        ) : !query.trim() ? (
          <div className='flex flex-1 items-center justify-center px-4 pb-[30px]'>
            <p
              className={twMerge(
                applyTypography('b12', startTypingText),
                'text-text-secondary',
              )}
            >
              {startTypingText}
            </p>
          </div>
        ) : filteredOptions.length === 0 ? (
          <div className='flex flex-1 items-center justify-center px-4 pb-[30px]'>
            {onUseQuery ? (
              <BaseButton
                type='text'
                size='m'
                htmlType='button'
                typo={false}
                isStatic
                gap='gap-[6px]'
                icon={
                  <PlusIcon
                    className='size-4 shrink-0 text-grayscale-gray07'
                    aria-hidden
                  />
                }
                onClick={() => onUseQuery(trimmedQuery)}
                textWrapperClass={twMerge(
                  'h-auto flex-none',
                  applyTypography('b14Strong', formatQueryLabel(trimmedQuery)),
                  'whitespace-nowrap',
                )}
                className='h-auto min-w-0 cursor-pointer items-center justify-center gap-[6px] rounded-[8px] border-0 bg-transparent px-3 py-[10px] text-grayscale-gray07 transition-colors duration-200 ease-out hover:bg-grayscale-gray01 hover:text-grayscale-gray07 active:bg-grayscale-gray02'
              >
                {formatQueryLabel(trimmedQuery)}
              </BaseButton>
            ) : (
              <p
                className={twMerge(
                  applyTypography('b12', noResultsText),
                  'text-text-secondary',
                )}
              >
                {noResultsText}
              </p>
            )}
          </div>
        ) : (
          <div className='flex flex-col flex-1 min-h-0 overflow-y-auto'>
            {filteredOptions.map((option) => {
              const isSelected = selectedValue === option.value;

              return (
                <BaseButton
                  key={`${type}-option-${option.value}`}
                  type='text'
                  size='m'
                  htmlType='button'
                  typo={false}
                  isStatic
                  leftalign
                  gap='gap-[6px]'
                  icon={
                    <LocationIcon16
                      className='size-4 shrink-0 text-text-secondary'
                      aria-hidden
                    />
                  }
                  onClick={() => onSelect(option.value, option)}
                  textWrapperClass={twMerge(
                    'h-auto',
                    applyTypography('b14', option.label),
                    'truncate text-text-primary',
                  )}
                  className={twMerge(
                    'h-[38px] w-full min-w-0 cursor-pointer items-center gap-[6px] rounded-[8px] border-0 bg-transparent px-3 py-[10px] text-left transition-colors duration-200 ease-out',
                    isSelected
                      ? 'bg-surface-lightgray60'
                      : 'hover:bg-grayscale-gray01 active:bg-grayscale-gray02',
                  )}
                >
                  {option.label}
                </BaseButton>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Mobile bottom-sheet variant of the city/country filter. Mirrors the popover
 * props/logic but renders as an animated sheet matching the Pathfinder mobile
 * Figma (search input + location-pin result rows + empty state).
 */
function PathfinderDetailSearchFilterSheet({
  isOpen,
  type,
  searchPlaceholder,
  query,
  onQueryChange,
  options,
  selectedValue,
  onSelect,
  onUseQuery,
  formatQueryLabel = (nextQuery) => `Use "${nextQuery}" as your ${type}`,
  loading = false,
  onClose,
}) {
  const t = useT();
  const typeLabelKo = type === 'city' ? '도시' : type === 'country' ? '국가' : type;
  const noResultsText = t('No results found', '검색 결과가 없어요');
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      const raf = requestAnimationFrame(() =>
        requestAnimationFrame(() => setAnimating(true)),
      );
      return () => cancelAnimationFrame(raf);
    }

    setAnimating(false);
    const timer = setTimeout(() => setVisible(false), 300);
    return () => clearTimeout(timer);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const raf = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [isOpen]);

  const trimmedQuery = query.trim();
  const typePlural =
    type === 'city' ? 'cities' : type === 'country' ? 'countries' : `${type}s`;
  const startTypingText = t(
    `Start typing to search ${typePlural}`,
    `검색어를 입력해 ${typeLabelKo}를 찾아보세요`,
  );
  const filteredOptions = useMemo(() => {
    const normalizedQuery = normalizeLabel(query);
    if (!normalizedQuery) return options;
    return options.filter((option) =>
      normalizeLabel(option.label).includes(normalizedQuery),
    );
  }, [options, query]);

  if (!visible) return null;

  return ReactDOM.createPortal(
    <div
      className='fixed inset-0 z-[10000] flex items-end justify-center pt-[60px]'
      style={{
        backgroundColor: animating ? 'rgba(85, 82, 78, 0.4)' : 'transparent',
        transition: 'background-color 0.3s ease',
      }}
      onClick={onClose}
    >
      <div
        className='flex h-[70dvh] max-h-[calc(100dvh-60px)] w-full flex-col overflow-hidden rounded-t-[40px] bg-grayscale-gray00 px-4 pb-10 pt-4'
        style={{
          transform: animating ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.3s ease',
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className='flex flex-col items-center pb-10 shrink-0'>
          <div className='h-[6px] w-[46px] rounded-[20px] bg-grayscale-gray02' />
        </div>

        <div className='flex h-12 w-full shrink-0 items-center gap-2 rounded-[12px] border border-grayscale-gray02 bg-surface-lightgray40 pl-4 pr-3'>
          <LocationIcon20
            className='size-5 shrink-0 text-text-placeholder'
            aria-hidden
          />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && trimmedQuery && onUseQuery) {
                event.preventDefault();
                onUseQuery(trimmedQuery);
              }
            }}
            placeholder={searchPlaceholder}
            className={twMerge(
              applyTypography('b16', query || searchPlaceholder),
              'w-full border-none bg-transparent p-0 text-text-primary outline-none placeholder:text-text-placeholder',
            )}
          />
          {query ? (
            <BaseButton
              type='text'
              size='s'
              htmlType='button'
              onClick={() => onQueryChange('')}
              className='size-5 min-w-0 shrink-0 rounded-[30px] border-0 p-0 bg-grayscale-gray01 text-text-placeholder transition-colors duration-200 ease-out hover:bg-grayscale-gray02 hover:text-text-placeholder'
              aria-label={`Clear ${type} search`}
              icon={<CancelIcon className='size-4' aria-hidden />}
            />
          ) : null}
        </div>

        <div className='flex flex-col flex-1 min-h-0 pt-2'>
          {loading ? (
            <div className='flex flex-1 items-center justify-center pb-[30px]'>
              <div className='size-10 animate-spin rounded-full border-[3px] border-grayscale-gray02 border-t-grayscale-gray04' />
            </div>
          ) : !trimmedQuery ? (
            <div className='flex flex-1 items-center justify-center px-4 pb-[30px]'>
              <p
                className={twMerge(
                  applyTypography('b12', startTypingText),
                  'text-text-secondary',
                )}
              >
                {startTypingText}
              </p>
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className='flex flex-1 items-center justify-center px-4 pb-[30px]'>
              {onUseQuery ? (
                <BaseButton
                  type='text'
                  size='m'
                  htmlType='button'
                  typo={false}
                  isStatic
                  gap='gap-[6px]'
                  icon={
                    <PlusIcon
                      className='size-4 shrink-0 text-grayscale-gray07'
                      aria-hidden
                    />
                  }
                  onClick={() => onUseQuery(trimmedQuery)}
                  textWrapperClass={twMerge(
                    'h-auto flex-none',
                    applyTypography(
                      'b14Strong',
                      formatQueryLabel(trimmedQuery),
                    ),
                    'whitespace-nowrap',
                  )}
                  className='h-auto min-w-0 cursor-pointer items-center justify-center gap-[6px] rounded-[8px] border-0 bg-transparent px-3 py-[10px] text-grayscale-gray07 transition-colors duration-200 ease-out hover:bg-grayscale-gray01 hover:text-grayscale-gray07 active:bg-grayscale-gray02'
                >
                  {formatQueryLabel(trimmedQuery)}
                </BaseButton>
              ) : (
                <p
                  className={twMerge(
                    applyTypography('b12', noResultsText),
                    'text-text-secondary',
                  )}
                >
                  {noResultsText}
                </p>
              )}
            </div>
          ) : (
            <div className='flex flex-col flex-1 min-h-0 p-2 overflow-y-auto'>
              {filteredOptions.map((option) => {
                const isSelected = selectedValue === option.value;

                return (
                  <BaseButton
                    key={`${type}-sheet-option-${option.value}`}
                    type='text'
                    size='m'
                    htmlType='button'
                    typo={false}
                    isStatic
                    leftalign
                    gap='gap-[6px]'
                    icon={
                      <LocationIcon16
                        className='size-4 shrink-0 text-text-secondary'
                        aria-hidden
                      />
                    }
                    onClick={() => onSelect(option.value, option)}
                    textWrapperClass={twMerge(
                      'h-auto',
                      applyTypography('b14', option.label),
                      'truncate text-text-primary',
                    )}
                    className={twMerge(
                      'h-auto min-h-[38px] w-full min-w-0 cursor-pointer items-center gap-[6px] rounded-[8px] border-0 bg-transparent px-3 py-[10px] text-left transition-colors duration-200 ease-out',
                      isSelected
                        ? 'bg-surface-lightgray60'
                        : 'hover:bg-grayscale-gray01 active:bg-grayscale-gray02',
                    )}
                  >
                    {option.label}
                  </BaseButton>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function PathfinderDetailSearchResultItem({
  title,
  subtitle,
  selected = false,
  onClick,
}) {
  return (
    <BaseButton
      type='text'
      size='m'
      htmlType='button'
      hideIcon
      typo={false}
      isStatic
      leftalign
      onClick={onClick}
      textWrapperClass='flex h-auto w-full flex-col items-start gap-[2px] text-left'
      className={twMerge(
        'h-auto w-full min-w-0 cursor-pointer flex-col items-start gap-[2px] rounded-[15px] border-0 bg-transparent p-2 text-left transition-colors duration-200 ease-out',
        selected
          ? 'bg-surface-lightgray60'
          : 'hover:bg-grayscale-gray01 active:bg-grayscale-gray02',
      )}
    >
      <span
        className={twMerge(
          applyTypography('b18', title),
          'w-full text-text-primary',
        )}
      >
        {title}
      </span>
      <span
        className={twMerge(
          applyTypography('b13', subtitle),
          'w-full text-text-placeholder',
        )}
      >
        {subtitle}
      </span>
    </BaseButton>
  );
}

function PathfinderDetailSearch({
  progressLabel,
  title,
  placeholder,
  query,
  defaultQuery = '',
  onQueryChange,
  cityOptions = [],
  cityValue,
  defaultCityValue = '',
  onCityChange,
  cityOptionsLoading = false,
  countryOptions = [],
  countryValue,
  defaultCountryValue = '',
  onCountryChange,
  countryOptionsLoading = false,
  results = [],
  resultsLoading = false,
  searchLoading = false,
  hasSearched = false,
  selectedValue,
  defaultSelectedValue = '',
  onSelectedChange,
  onSearch,
  hasMoreResults = false,
  moreResultsLoading = false,
  onLoadMore,
  searchButtonText,
  selectedLabel,
  emptySelectedText,
  resultsLabel,
  useQueryLabel,
  className = '',
}) {
  const t = useT();
  const resolvedPlaceholder =
    placeholder ?? t(DEFAULT_SEARCH_PLACEHOLDER, '학교를 검색해 주세요');
  const resolvedSearchButtonText = searchButtonText ?? t('Search', '검색');
  const resolvedSelectedLabel = selectedLabel ?? t('Selected :', '선택한 항목 :');
  const resolvedEmptySelectedText =
    emptySelectedText ?? t('No added yet', '아직 추가한 항목이 없어요');
  const resolvedResultsLabel = resultsLabel ?? t('Result :', '검색 결과 :');
  const formatUseQueryLabel =
    useQueryLabel ??
    ((nextQuery) =>
      t(
        `Use "${nextQuery}" as your school`,
        `"${nextQuery}"을(를) 내 학교로 사용하기`,
      ));
  const noResultsText = t('No results found', '검색 결과가 없어요');
  const rootRef = useRef(null);
  const cityInputRef = useRef(null);
  const countryInputRef = useRef(null);

  const [internalQuery, setInternalQuery] = useState(defaultQuery);
  const [internalCityValue, setInternalCityValue] = useState(defaultCityValue);
  const [internalCountryValue, setInternalCountryValue] =
    useState(defaultCountryValue);
  const [internalSelectedValue, setInternalSelectedValue] =
    useState(defaultSelectedValue);
  const [manualSelectedTitle, setManualSelectedTitle] = useState('');
  const [activeFilter, setActiveFilter] = useState(null);
  const [citySearchQuery, setCitySearchQuery] = useState('');
  const [countrySearchQuery, setCountrySearchQuery] = useState('');
  const isMobile = useIsMobile();

  const normalizedCityOptions = useMemo(
    () => cityOptions.map(normalizeChoice).filter((option) => option.label),
    [cityOptions],
  );
  const normalizedCountryOptions = useMemo(
    () => countryOptions.map(normalizeChoice).filter((option) => option.label),
    [countryOptions],
  );
  const normalizedResults = useMemo(
    () => results.map(normalizeResult).filter((result) => result.title),
    [results],
  );

  const isControlledQuery = query !== undefined;
  const currentQuery = isControlledQuery ? query : internalQuery;
  const isControlledCity = cityValue !== undefined;
  const currentCityValue = isControlledCity ? cityValue : internalCityValue;
  const isControlledCountry = countryValue !== undefined;
  const currentCountryValue = isControlledCountry
    ? countryValue
    : internalCountryValue;
  const isControlledSelected = selectedValue !== undefined;
  const currentSelectedValue = isControlledSelected
    ? selectedValue
    : internalSelectedValue;

  const cityLabel = useMemo(
    () =>
      normalizedCityOptions.find((option) => option.value === currentCityValue)
        ?.label ??
      (typeof currentCityValue === 'string' ? currentCityValue : ''),
    [currentCityValue, normalizedCityOptions],
  );
  const countryLabel = useMemo(
    () =>
      normalizedCountryOptions.find(
        (option) => option.value === currentCountryValue,
      )?.label ??
      (typeof currentCountryValue === 'string' ? currentCountryValue : ''),
    [currentCountryValue, normalizedCountryOptions],
  );
  const selectedResult = useMemo(
    () =>
      normalizedResults.find(
        (result) => result.value === currentSelectedValue,
      ) ?? null,
    [currentSelectedValue, normalizedResults],
  );
  const selectedResultTitle = selectedResult?.title ?? manualSelectedTitle;

  const setQueryState = useCallback(
    (nextQuery) => {
      if (!isControlledQuery) {
        setInternalQuery(nextQuery);
      }

      onQueryChange?.(nextQuery);
    },
    [isControlledQuery, onQueryChange],
  );

  const setCityState = useCallback(
    (nextValue, option) => {
      if (!isControlledCity) {
        setInternalCityValue(nextValue);
      }

      onCityChange?.(nextValue, option);
    },
    [isControlledCity, onCityChange],
  );

  const setCountryState = useCallback(
    (nextValue, option) => {
      if (!isControlledCountry) {
        setInternalCountryValue(nextValue);
      }

      onCountryChange?.(nextValue, option);
    },
    [isControlledCountry, onCountryChange],
  );

  const setSelectedState = useCallback(
    (nextValue, result) => {
      if (!isControlledSelected) {
        setInternalSelectedValue(nextValue);
      }

      onSelectedChange?.(nextValue, result);
    },
    [isControlledSelected, onSelectedChange],
  );

  const handleSearch = useCallback(() => {
    onSearch?.({
      query: currentQuery.trim(),
      cityValue: currentCityValue,
      countryValue: currentCountryValue,
    });
  }, [currentCityValue, currentCountryValue, currentQuery, onSearch]);

  const handleResultSelect = useCallback(
    (result) => {
      setManualSelectedTitle(
        normalizedResults.some((item) => item.value === result.value)
          ? ''
          : result.title,
      );
      setSelectedState(result.value, result);
    },
    [normalizedResults, setSelectedState],
  );

  useEffect(() => {
    // Mobile renders the filter as a portal bottom-sheet (outside rootRef), so
    // its own overlay handles dismissal — this desktop popover guard would
    // otherwise close the sheet the moment it is tapped.
    if (isMobile) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setActiveFilter(null);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [isMobile]);

  useEffect(() => {
    if (activeFilter === 'city') {
      requestAnimationFrame(() => {
        cityInputRef.current?.focus();
      });
    }

    if (activeFilter === 'country') {
      requestAnimationFrame(() => {
        countryInputRef.current?.focus();
      });
    }
  }, [activeFilter]);

  useEffect(() => {
    if (!currentSelectedValue) {
      setManualSelectedTitle('');
    }
  }, [currentSelectedValue]);

  const hasResults = normalizedResults.length > 0;
  const showNoResults = hasSearched && !resultsLoading && !hasResults;
  const showUseQueryButton = Boolean(currentQuery.trim());

  return (
    <div
      ref={rootRef}
      className={twMerge('flex w-full flex-col items-start', className)}
    >
      {progressLabel || title ? (
        <PathfinderQuestionHeader
          progressLabel={progressLabel}
          title={title}
          className='pb-[50px]'
        />
      ) : null}

      <div className='flex w-full items-center gap-[6px] px-2 pb-6'>
        <span
          className={twMerge(
            applyTypography('b14Strong', selectedLabel),
            'text-text-placeholder',
          )}
        >
          {selectedLabel}
        </span>
        {selectedResult ? (
          <span
            className={twMerge(
              applyTypography('b14Strong', selectedResultTitle),
              'text-green-deep02',
            )}
          >
            {selectedResultTitle}
          </span>
        ) : selectedResultTitle ? (
          <span
            className={twMerge(
              applyTypography('b14Strong', selectedResultTitle),
              'text-green-deep02',
            )}
          >
            {selectedResultTitle}
          </span>
        ) : (
          <span
            className={twMerge(
              applyTypography('b12', emptySelectedText),
              'text-text-placeholder',
            )}
          >
            {emptySelectedText}
          </span>
        )}
      </div>

      <div className='flex flex-col w-full gap-4 pb-10'>
        <div className='group relative flex h-[80px] w-full items-center gap-2 rounded-[15px] bg-surface-lightgray60 pl-4 pr-3'>
          <SearchIcon
            className='transition-colors duration-200 ease-out size-5 shrink-0 text-text-placeholder group-hover:text-green-deep02'
            aria-hidden
          />
          <input
            value={currentQuery}
            onChange={(event) => setQueryState(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleSearch();
              }
            }}
            placeholder={placeholder}
            className={twMerge(
              applyTypography('b16', currentQuery || placeholder),
              'w-full border-none bg-transparent p-0 text-text-primary outline-none placeholder:text-text-placeholder',
            )}
          />
          {currentQuery ? (
            <BaseButton
              type='text'
              size='s'
              htmlType='button'
              onClick={() => setQueryState('')}
              className='size-5 min-w-0 shrink-0 rounded-[30px] border-0 p-0 bg-grayscale-gray01 text-text-placeholder transition-colors duration-200 ease-out hover:bg-grayscale-gray02 hover:text-text-placeholder'
              aria-label='Clear school search'
              icon={<CancelIcon className='size-4' aria-hidden />}
            />
          ) : null}
        </div>

        <div className='flex items-start justify-between w-full gap-3'>
          <div className='flex flex-wrap items-start flex-1 min-w-0 gap-2'>
            <div className='relative'>
              <PathfinderDetailSearchFilterTrigger
                label={cityLabel}
                placeholder='All cities'
                active={activeFilter === 'city'}
                onClick={() =>
                  setActiveFilter((prev) => (prev === 'city' ? null : 'city'))
                }
              />
              {!isMobile && activeFilter === 'city' ? (
                <PathfinderDetailSearchFilterPopover
                  type='city'
                  searchPlaceholder='Search city'
                  query={citySearchQuery}
                  onQueryChange={setCitySearchQuery}
                  options={normalizedCityOptions}
                  selectedValue={currentCityValue}
                  onSelect={(nextValue, option) => {
                    setCityState(nextValue, option);
                    setCitySearchQuery('');
                    setActiveFilter(null);
                  }}
                  onUseQuery={(nextQuery) => {
                    setCityState(nextQuery, {
                      value: nextQuery,
                      label: nextQuery,
                    });
                    setCitySearchQuery('');
                    setActiveFilter(null);
                  }}
                  formatQueryLabel={(nextQuery) =>
                    `Use "${nextQuery}" as your city`
                  }
                  loading={cityOptionsLoading}
                  inputRef={cityInputRef}
                  className='w-[300px]'
                />
              ) : null}
            </div>

            <div className='relative'>
              <PathfinderDetailSearchFilterTrigger
                label={countryLabel}
                placeholder='All countries'
                active={activeFilter === 'country'}
                onClick={() =>
                  setActiveFilter((prev) =>
                    prev === 'country' ? null : 'country',
                  )
                }
              />
              {!isMobile && activeFilter === 'country' ? (
                <PathfinderDetailSearchFilterPopover
                  type='country'
                  searchPlaceholder='Search country'
                  query={countrySearchQuery}
                  onQueryChange={setCountrySearchQuery}
                  options={normalizedCountryOptions}
                  selectedValue={currentCountryValue}
                  onSelect={(nextValue, option) => {
                    setCountryState(nextValue, option);
                    setCountrySearchQuery('');
                    setActiveFilter(null);
                  }}
                  onUseQuery={(nextQuery) => {
                    setCountryState(nextQuery, {
                      value: nextQuery,
                      label: nextQuery,
                    });
                    setCountrySearchQuery('');
                    setActiveFilter(null);
                  }}
                  formatQueryLabel={(nextQuery) =>
                    `Use "${nextQuery}" as your country`
                  }
                  loading={countryOptionsLoading}
                  inputRef={countryInputRef}
                  className='w-[300px]'
                />
              ) : null}
            </div>
          </div>

          <BaseButton
            type='primary'
            size='l'
            width='120px'
            hideIcon
            isLoading={searchLoading}
            onClick={handleSearch}
          >
            {searchButtonText}
          </BaseButton>
        </div>
      </div>

      <div className='flex flex-col items-start w-full bg-grayscale-gray00'>
        <div className='flex items-center w-full px-2 pb-4'>
          <span
            className={twMerge(
              applyTypography('b14Strong', resultsLabel),
              'text-text-placeholder',
            )}
          >
            {resultsLabel}
          </span>
        </div>

        {resultsLoading ? (
          <div className='flex flex-col w-full gap-3'>
            {Array.from({ length: 4 }, (_, index) => (
              <div
                key={`detail-search-skeleton-${index}`}
                className='flex h-[63px] w-full flex-col gap-2 rounded-[15px] p-2'
              >
                <div className='h-5 w-[280px] rounded-[8px] bg-surface-lightgray60' />
                <div className='h-3 w-[200px] rounded-[6px] bg-surface-lightgray60' />
              </div>
            ))}
          </div>
        ) : showNoResults ? (
          <div className='flex flex-col items-center w-full px-4 pt-20'>
            <p
              className={twMerge(
                applyTypography('b13', 'No results found'),
                'text-text-placeholder',
              )}
            >
              No results found
            </p>
            {showUseQueryButton ? (
              <BaseButton
                type='text'
                size='m'
                htmlType='button'
                typo={false}
                isStatic
                gap='gap-[6px]'
                icon={
                  <PlusIcon
                    className='size-4 shrink-0 text-grayscale-gray07'
                    aria-hidden
                  />
                }
                onClick={() =>
                  handleResultSelect({
                    value: `manual-${normalizeLabel(currentQuery).replace(/\s+/g, '-')}`,
                    title: currentQuery.trim(),
                    subtitle: '',
                  })
                }
                textWrapperClass={twMerge(
                  'h-auto flex-none',
                  applyTypography(
                    'b14Strong',
                    formatUseQueryLabel(currentQuery.trim()),
                  ),
                  'whitespace-nowrap',
                )}
                className='mt-2 h-[38px] min-w-0 cursor-pointer items-center justify-center gap-[6px] rounded-[8px] border-0 bg-transparent px-3 py-[10px] text-grayscale-gray07 transition-colors duration-200 ease-out hover:bg-grayscale-gray01 hover:text-grayscale-gray07 active:bg-grayscale-gray02'
              >
                {formatUseQueryLabel(currentQuery.trim())}
              </BaseButton>
            ) : null}
          </div>
        ) : hasResults ? (
          <div className='flex flex-col w-full'>
            <div
              className={twMerge(
                'flex w-full flex-col gap-3',
                normalizedResults.length > 6
                  ? 'max-h-[462px] overflow-y-auto pr-1'
                  : '',
              )}
            >
              {normalizedResults.map((result) => (
                <PathfinderDetailSearchResultItem
                  key={`detail-search-result-${result.value}`}
                  title={result.title}
                  subtitle={result.subtitle}
                  selected={currentSelectedValue === result.value}
                  onClick={() => handleResultSelect(result)}
                />
              ))}
            </div>

            {moreResultsLoading ? (
              <div className='flex items-center justify-center w-full pt-4'>
                <span
                  className={twMerge(
                    applyTypography('b16Strong', 'More on the way...'),
                    'text-grayscale-gray05',
                  )}
                >
                  More on the way...
                </span>
              </div>
            ) : hasMoreResults ? (
              <div className='flex items-center justify-center w-full pt-4'>
                <BaseButton
                  type='text'
                  size='m'
                  htmlType='button'
                  typo={false}
                  isStatic
                  gap='gap-2'
                  iconPosition='after'
                  icon={
                    <ArrowBottomIcon
                      className='size-5 shrink-0 text-grayscale-gray07'
                      aria-hidden
                    />
                  }
                  onClick={onLoadMore}
                  textWrapperClass={twMerge(
                    'h-auto flex-none',
                    applyTypography('b16Strong', 'More results'),
                    'whitespace-nowrap',
                  )}
                  className='h-auto min-w-0 cursor-pointer items-center justify-center gap-2 rounded-[50px] border-0 bg-transparent px-4 py-[14px] text-grayscale-gray07 transition-colors duration-200 ease-out hover:bg-grayscale-gray01 hover:text-grayscale-gray07 active:bg-grayscale-gray02'
                >
                  More results
                </BaseButton>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <PathfinderDetailSearchFilterSheet
        isOpen={isMobile && activeFilter === 'city'}
        type='city'
        searchPlaceholder='Search city'
        query={citySearchQuery}
        onQueryChange={setCitySearchQuery}
        options={normalizedCityOptions}
        selectedValue={currentCityValue}
        onSelect={(nextValue, option) => {
          setCityState(nextValue, option);
          setCitySearchQuery('');
          setActiveFilter(null);
        }}
        onUseQuery={(nextQuery) => {
          setCityState(nextQuery, { value: nextQuery, label: nextQuery });
          setCitySearchQuery('');
          setActiveFilter(null);
        }}
        formatQueryLabel={(nextQuery) => `Use "${nextQuery}" as your city`}
        loading={cityOptionsLoading}
        onClose={() => setActiveFilter(null)}
      />

      <PathfinderDetailSearchFilterSheet
        isOpen={isMobile && activeFilter === 'country'}
        type='country'
        searchPlaceholder='Search country'
        query={countrySearchQuery}
        onQueryChange={setCountrySearchQuery}
        options={normalizedCountryOptions}
        selectedValue={currentCountryValue}
        onSelect={(nextValue, option) => {
          setCountryState(nextValue, option);
          setCountrySearchQuery('');
          setActiveFilter(null);
        }}
        onUseQuery={(nextQuery) => {
          setCountryState(nextQuery, { value: nextQuery, label: nextQuery });
          setCountrySearchQuery('');
          setActiveFilter(null);
        }}
        formatQueryLabel={(nextQuery) => `Use "${nextQuery}" as your country`}
        loading={countryOptionsLoading}
        onClose={() => setActiveFilter(null)}
      />
    </div>
  );
}

export default PathfinderDetailSearch;
